
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const [activeSection, setActiveSection] = useState('account');
  const [settings, setSettings] = useState({
    // Account
    email: 'user@example.com',
    phone: '+1 555 123 4567',
    
    // Privacy
    profileVisibility: 'public',
    showOnline: true,
    showDistance: true,
    
    // Notifications
    emailNotifications: true,
    pushNotifications: true,
    matchNotifications: true,
    messageNotifications: true,
    
    // Preferences
    ageMin: 25,
    ageMax: 35,
    maxDistance: 100,
    showMe: 'everyone',
  });

  const [passwordFields, setPasswordFields] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const menuItems = [
    { id: 'account', icon: 'ri-user-line', label: t('settings.account') },
    { id: 'privacy', icon: 'ri-shield-line', label: t('settings.privacy') },
    { id: 'notifications', icon: 'ri-notification-line', label: t('settings.notifications') },
    { id: 'preferences', icon: 'ri-settings-3-line', label: t('settings.preferences') },
    { id: 'security', icon: 'ri-lock-line', label: t('settings.security') },
    { id: 'language', icon: 'ri-global-line', label: t('settings.language') },
    { id: 'help', icon: 'ri-question-line', label: t('settings.help') },
  ];

  const changeLanguage = (lng: string) => {
    try {
      i18n.changeLanguage(lng);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const handleAgeRangeChange = (field: 'ageMin' | 'ageMax', value: string) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 18 && numValue <= 100) {
      setSettings(prev => {
        const newSettings = { ...prev, [field]: numValue };
        // Ensure ageMin is not greater than ageMax
        if (field === 'ageMin' && numValue > prev.ageMax) {
          newSettings.ageMax = numValue;
        } else if (field === 'ageMax' && numValue < prev.ageMin) {
          newSettings.ageMin = numValue;
        }
        return newSettings;
      });
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-8">
            {t('settings.title')}
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Menu */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-4">
                <nav className="space-y-2">
                  {menuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                        activeSection === item.id
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`${item.icon} text-lg`}></i>
                      {item.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                {/* Account Settings */}
                {activeSection === 'account' && (
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                      {t('settings.accountSettings.title')}
                    </h2>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.accountSettings.email')}
                        </label>
                        <input
                          type="email"
                          value={settings.email}
                          onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.accountSettings.phone')}
                        </label>
                        <input
                          type="tel"
                          value={settings.phone}
                          onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      <div className="pt-4">
                        <button className="px-6 py-3 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                          {t('settings.accountSettings.saveChanges')}
                        </button>
                      </div>

                      <div className="pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.accountSettings.dangerZone')}</h3>
                        <button className="px-6 py-3 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer">
                          {t('settings.accountSettings.deleteAccount')}
                        </button>
                        <p className="text-xs text-gray-600 mt-2">
                          {t('settings.accountSettings.deleteWarning')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Privacy Settings */}
                {activeSection === 'privacy' && (
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                      {t('settings.privacySettings.title')}
                    </h2>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.privacySettings.profileVisibility')}
                        </label>
                        <select
                          value={settings.profileVisibility}
                          onChange={(e) => setSettings({ ...settings, profileVisibility: e.target.value })}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="public">{t('settings.privacySettings.public')}</option>
                          <option value="matches">{t('settings.privacySettings.matchesOnly')}</option>
                          <option value="private">{t('settings.privacySettings.private')}</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-between py-4 border-b border-gray-200">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{t('settings.privacySettings.showOnline')}</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('settings.privacySettings.showOnlineDesc')}
                          </p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, showOnline: !settings.showOnline })}
                          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                            settings.showOnline ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                              settings.showOnline ? 'translate-x-6' : 'translate-x-0'
                            }`}
                          ></span>
                        </button>
                      </div>

                      <div className="flex items-center justify-between py-4 border-b border-gray-200">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{t('settings.privacySettings.showDistance')}</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('settings.privacySettings.showDistanceDesc')}
                          </p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, showDistance: !settings.showDistance })}
                          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                            settings.showDistance ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                              settings.showDistance ? 'translate-x-6' : 'translate-x-0'
                            }`}
                          ></span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notification Settings */}
                {activeSection === 'notifications' && (
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                      {t('settings.notificationSettings.title')}
                    </h2>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-4 border-b border-gray-200">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{t('settings.notificationSettings.emailNotifications')}</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('settings.notificationSettings.emailDesc')}
                          </p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, emailNotifications: !settings.emailNotifications })}
                          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                            settings.emailNotifications ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                              settings.emailNotifications ? 'translate-x-6' : 'translate-x-0'
                            }`}
                          ></span>
                        </button>
                      </div>

                      <div className="flex items-center justify-between py-4 border-b border-gray-200">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{t('settings.notificationSettings.pushNotifications')}</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('settings.notificationSettings.pushDesc')}
                          </p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, pushNotifications: !settings.pushNotifications })}
                          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                            settings.pushNotifications ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                              settings.pushNotifications ? 'translate-x-6' : 'translate-x-0'
                            }`}
                          ></span>
                        </button>
                      </div>

                      <div className="flex items-center justify-between py-4 border-b border-gray-200">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{t('settings.notificationSettings.matchNotifications')}</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('settings.notificationSettings.matchDesc')}
                          </p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, matchNotifications: !settings.matchNotifications })}
                          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                            settings.matchNotifications ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                              settings.matchNotifications ? 'translate-x-6' : 'translate-x-0'
                            }`}
                          ></span>
                        </button>
                      </div>

                      <div className="flex items-center justify-between py-4 border-b border-gray-200">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{t('settings.notificationSettings.messageNotifications')}</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {t('settings.notificationSettings.messageDesc')}
                          </p>
                        </div>
                        <button
                          onClick={() => setSettings({ ...settings, messageNotifications: !settings.messageNotifications })}
                          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                            settings.messageNotifications ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                              settings.messageNotifications ? 'translate-x-6' : 'translate-x-0'
                            }`}
                          ></span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preferences */}
                {activeSection === 'preferences' && (
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                      {t('settings.preferenceSettings.title')}
                    </h2>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.preferenceSettings.ageRange')}
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="number"
                            value={settings.ageMin}
                            onChange={(e) => handleAgeRangeChange('ageMin', e.target.value)}
                            className="w-24 px-4 py-3 text-sm border border-gray-300 rounded-lg"
                            min="18"
                            max="100"
                          />
                          <span className="text-gray-500">-</span>
                          <input
                            type="number"
                            value={settings.ageMax}
                            onChange={(e) => handleAgeRangeChange('ageMax', e.target.value)}
                            className="w-24 px-4 py-3 text-sm border border-gray-300 rounded-lg"
                            min="18"
                            max="100"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.preferenceSettings.maxDistance')}: {settings.maxDistance} {t('common.km')}
                        </label>
                        <input
                          type="range"
                          value={settings.maxDistance}
                          onChange={(e) => setSettings({ ...settings, maxDistance: parseInt(e.target.value) })}
                          className="w-full"
                          min="10"
                          max="10000"
                          step="10"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.preferenceSettings.showMe')}
                        </label>
                        <select
                          value={settings.showMe}
                          onChange={(e) => setSettings({ ...settings, showMe: e.target.value })}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="everyone">{t('settings.preferenceSettings.everyone')}</option>
                          <option value="women">{t('settings.preferenceSettings.womenOnly')}</option>
                          <option value="men">{t('settings.preferenceSettings.menOnly')}</option>
                        </select>
                      </div>

                      <div className="pt-4">
                        <button className="px-6 py-3 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                          {t('settings.preferenceSettings.savePreferences')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Security */}
                {activeSection === 'security' && (
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                      {t('settings.securitySettings.title')}
                    </h2>
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.securitySettings.changePassword')}</h3>
                        <div className="space-y-4">
                          <input
                            type="password"
                            placeholder={t('settings.securitySettings.currentPassword')}
                            value={passwordFields.currentPassword}
                            onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                            className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <input
                            type="password"
                            placeholder={t('settings.securitySettings.newPassword')}
                            value={passwordFields.newPassword}
                            onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                            className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <input
                            type="password"
                            placeholder={t('settings.securitySettings.confirmNewPassword')}
                            value={passwordFields.confirmNewPassword}
                            onChange={(e) => handlePasswordChange('confirmNewPassword', e.target.value)}
                            className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <button className="px-6 py-3 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                            {t('settings.securitySettings.updatePassword')}
                          </button>
                        </div>
                      </div>

                      <div className="pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.securitySettings.twoFactor')}</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          {t('settings.securitySettings.twoFactorDesc')}
                        </p>
                        <button className="px-6 py-3 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer">
                          {t('settings.securitySettings.enable')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Language Settings */}
                {activeSection === 'language' && (
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                      {t('settings.languageSettings.title')}
                    </h2>
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.languageSettings.selectLanguage')}
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(t('settings.languageSettings.languages', { returnObjects: true }) as Record<string, string>).map(([code, name]) => (
                          <button
                            key={code}
                            onClick={() => changeLanguage(code)}
                            className={`flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all cursor-pointer ${
                              i18n.language === code
                                ? 'border-primary-600 bg-primary-50 text-primary-700'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
                            }`}
                          >
                            <span>{name}</span>
                            {i18n.language === code && (
                              <i className="ri-check-line text-primary-600"></i>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Help */}
                {activeSection === 'help' && (
                  <div>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                      {t('settings.helpSettings.title')}
                    </h2>
                    <div className="space-y-4">
                      <a
                        href="#"
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <i className="ri-question-line text-xl text-primary-600"></i>
                          <span className="text-sm font-medium text-gray-900">{t('settings.helpSettings.faq')}</span>
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </a>

                      <a
                        href="#"
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <i className="ri-customer-service-line text-xl text-primary-600"></i>
                          <span className="text-sm font-medium text-gray-900">{t('settings.helpSettings.support')}</span>
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </a>

                      <a
                        href="#"
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <i className="ri-feedback-line text-xl text-primary-600"></i>
                          <span className="text-sm font-medium text-gray-900">{t('settings.helpSettings.feedback')}</span>
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </a>

                      <a
                        href="#"
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <i className="ri-file-text-line text-xl text-primary-600"></i>
                          <span className="text-sm font-medium text-gray-900">{t('settings.helpSettings.guide')}</span>
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SettingsPage;
